import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useLogService } from '../../../../hooks/useLogService';
import { useTranslate } from '../../../../hooks/useTranslate';
import { useMarket } from '../../../../providers/MarketProvider';
import { InputField } from '../../../Form/components/InputField/InputField';
import { Error } from '../../../Form/Form.styles';
import {
  ButtonsWrapper,
  FieldsWrapper,
  StyledButton,
  StyledForm,
} from '../../AuthModal.styles';
import { P, Result } from './ForgottenPasswordForm.styles';
import { ResetPasswordFormFields } from './ForgottenPasswordForm.types';
import { performResetPassword } from './ForgottenPasswordForm.utils';

type Props = {
  onCancel: () => void;
};

export const ForgottenPasswordForm = ({ onCancel }: Props) => {
  const { t } = useTranslate();
  const market = useMarket();
  const { logError } = useLogService();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [requestedSuccessfully, setRequestedSuccessfully] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormFields>();

  const onSubmit = async (data: ResetPasswordFormFields) => {
    setIsLoading(true);
    setIsError(false);

    try {
      const response = await performResetPassword(data);
      if (response.ok) {
        return setRequestedSuccessfully(true);
      }

      setIsError(true);
    } catch (e) {
      setIsError(true);
      logError(e, {
        extra: {
          market,
          location: 'share-widget',
          email: data.email,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (requestedSuccessfully) {
    return (
      <Result>
        <P>{t('ui.action.forgottenPassword.requestSent')}</P>

        <P>{t('ui.action.forgottenPassword.clickToLoginText')}</P>

        <ButtonsWrapper>
          <StyledButton variant="primary" size="large" onClick={onCancel}>
            {t('ui.action.forgottenPassword.logIn')}
          </StyledButton>
        </ButtonsWrapper>
      </Result>
    );
  }

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <FieldsWrapper>
        <InputField
          type="email"
          htmlId="auth-modal-forgotten-password-email"
          label={t('ui.action.forgottenPassword.email.label')}
          placeholder={t('ui.action.forgottenPassword.email.placeholder')}
          autoFocus
          data-autofocus
          error={errors.email?.message}
          {...register('email', {
            required: t('ui.action.forgottenPassword.email.requiredError'),
          })}
        />

        {isError && (
          <Error>{t('ui.action.forgottenPassword.genericError')}</Error>
        )}
      </FieldsWrapper>

      <ButtonsWrapper>
        <StyledButton
          type="submit"
          variant="primary"
          size="large"
          disabled={isLoading}
        >
          {isLoading
            ? t('ui.action.forgottenPassword.submitButton.loading')
            : t('ui.action.forgottenPassword.submitButton.label')}
        </StyledButton>

        <StyledButton variant="secondary" size="large" onClick={onCancel}>
          {t('ui.action.forgottenPassword.cancel')}
        </StyledButton>
      </ButtonsWrapper>
    </StyledForm>
  );
};
